import { useEffect, useState } from 'react';
import { Alert, Card, ProgressBar } from 'react-bootstrap';
import { AxiosError } from 'axios';
import axiosInstance from 'utils/axiosInterceptors';
import { formatNumberWithCommas } from 'utils/formats';
import { TProcurementLimitWarning } from 'utils/types';

type ProcurementLimitWarningProps = {
  branchDomain?: string;
  variant: 'summary' | 'purchaseOrder';
};

const formatPeso = (value: number | null | undefined) =>
  `₱${formatNumberWithCommas(value ?? 0)}`;

const formatMonthLabel = (value: string | undefined) => {
  if (!value) {
    return 'this month';
  }

  const [year, month] = value.substring(0, 10).split('-').map(Number);
  if (!year || !month) {
    return 'this month';
  }

  return new Date(year, month - 1, 1).toLocaleString('en-US', {
    month: 'long',
    year: 'numeric'
  });
};

const getUsagePercent = (used: number, limit: number) => {
  if (limit <= 0) {
    return 0;
  }

  return Math.min(100, (used / limit) * 100);
};

const ProcurementLimitWarning = ({
  branchDomain,
  variant
}: ProcurementLimitWarningProps) => {
  const [warning, setWarning] = useState<TProcurementLimitWarning | null>(null);

  const loadWarning = async () => {
    if (!branchDomain) {
      return;
    }

    try {
      const { data } = await axiosInstance.get<TProcurementLimitWarning>(
        `/api/v1/${branchDomain}/procurementLimit`
      );
      setWarning(data);
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status !== 403) {
        console.error('Failed to load procurement limit', error);
      }
    }
  };

  useEffect(() => {
    void loadWarning();
  }, [branchDomain]);

  if (!warning) {
    return null;
  }

  const monthLabel = formatMonthLabel(warning.currentMonth);
  const previousMonthLabel = formatMonthLabel(warning.previousMonth);
  const limitSource = warning.isUsingFallbackLimit
    ? `default monthly limit (no recovered cost in ${previousMonthLabel})`
    : `${previousMonthLabel} recovered cost (store and shop)`;
  const usagePercent = getUsagePercent(
    warning.currentMonthPurchaseAmount,
    warning.procurementLimit
  );
  const remaining = warning.procurementLimit - warning.currentMonthPurchaseAmount;
  const progressVariant = warning.isLimitExceeded
    ? 'danger'
    : usagePercent >= 80
      ? 'warning'
      : 'primary';
  const showWarning =
    warning.isLimitExceeded ||
    (variant === 'purchaseOrder' && warning.wouldExceedIfPendingOrdered);

  if (variant === 'summary') {
    return (
      <Card className="mb-3">
        <Card.Body className="py-3">
          <div className="fs-10 text-body-tertiary text-uppercase mb-1">
            {monthLabel} vs {limitSource}
          </div>
          <div className="d-flex align-items-baseline flex-wrap gap-2">
            <span
              className={`fs-6 fw-semibold ${
                warning.isLimitExceeded ? 'text-danger' : ''
              }`}
            >
              {formatPeso(warning.currentMonthPurchaseAmount)}
            </span>
            <span className="fs-9 text-body-tertiary">
              of {formatPeso(warning.procurementLimit)}
            </span>
          </div>
          <ProgressBar
            now={usagePercent}
            variant={progressVariant}
            className="mt-2"
            style={{ height: 8 }}
          />
          <div className="fs-9 text-body-tertiary mt-2">
            {warning.isLimitExceeded
              ? `Over the limit by ${formatPeso(Math.abs(remaining))}`
              : `${formatPeso(remaining)} remaining this month`}
            {'. '}
            Limit is {formatPeso(warning.procurementLimit)} from {limitSource}.
          </div>
          {showWarning && (
            <Alert variant="warning" className="mb-0 mt-3">
              {monthLabel} purchases {formatPeso(warning.currentMonthPurchaseAmount)}{' '}
              already exceed the {formatPeso(warning.procurementLimit)}{' '}
              {limitSource}.
            </Alert>
          )}
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="mb-3">
      {showWarning && (
        <Alert variant="warning" className="mb-0">
          {warning.isLimitExceeded ? (
            <div>
              {monthLabel} purchases {formatPeso(warning.currentMonthPurchaseAmount)}{' '}
              already exceed the {formatPeso(warning.procurementLimit)}{' '}
              {limitSource}.
            </div>
          ) : (
            <div>
              {monthLabel} purchases are {formatPeso(warning.currentMonthPurchaseAmount)}{' '}
              of the {formatPeso(warning.procurementLimit)} {limitSource}.
            </div>
          )}
          <div className="mt-2">
            If the unfulfilled purchase orders are received (est.{' '}
            {formatPeso(warning.pendingPurchaseOrderEstimate)}
            {warning.pendingPurchaseOrderCount > 0
              ? ` across ${warning.pendingPurchaseOrderCount} PO${
                  warning.pendingPurchaseOrderCount === 1 ? '' : 's'
                }`
              : ''}
            ), the potential monthly procurement total would be{' '}
            <strong>{formatPeso(warning.potentialTotalAmount)}</strong>
            {warning.wouldExceedIfPendingOrdered
              ? `, which exceeds the ${formatPeso(warning.procurementLimit)} limit.`
              : '.'}
          </div>
        </Alert>
      )}

      {!showWarning && (
        <Alert variant="info" className="mb-0">
          Monthly procurement limit: {formatPeso(warning.procurementLimit)} (
          {limitSource}). {monthLabel} purchases:{' '}
          {formatPeso(warning.currentMonthPurchaseAmount)}. If unfulfilled POs
          are received (est.{' '}
          {formatPeso(warning.pendingPurchaseOrderEstimate)}), potential monthly
          total is {formatPeso(warning.potentialTotalAmount)}.
        </Alert>
      )}
    </div>
  );
};

export default ProcurementLimitWarning;
